// Intensity grid derived from the EDITOR Draft (programEdit.ts) instead
// of a stored Program — the composer's live preview recolours on every
// block commit, mid-edit, so it can't go through buildPayload (which
// rejects incomplete drafts). Tolerant by design: an unparsable value or
// a value-less kind simply contributes no band, it never throws.
//
// Same band semantics as programIntensity.ts (the stored-Program twin):
// hardest set wins the (day, week) cell; %-of-MAX or effort both colour;
// `max` stays reserved for %-of-MAX ≥ 85.

import type { Draft, SlotDraft } from "./programEdit.js";
import { setCountAt } from "./programEditRoundSets.js";
import {
  BAND_RANK,
  bandOf,
  higherBand,
  rpeBand,
  rirBand,
  type IntensityBand,
  type IntensityGrid,
  type WeekCell,
} from "./programIntensity.js";
import { readRound, type ReadSet } from "./programProgression.js";
import type { MaxForProposal } from "./resolveDefaultWeight.js";
import type { UnitsPreference } from "./schemas/profile.js";
import { toCanonicalKg } from "./units.js";

const fmt = (n: number): string => String(Math.round(n * 100) / 100);

/** What a row in kilos needs to take a band (#336, brief §15 row 28): the
 *  athlete's MAX, and the unit the cells are written in. */
export type BandReference = { max: MaxForProposal | undefined; cellUnit: UnitsPreference };

/** A band and the number that drives it. */
export type RoundBand = { band: IntensityBand; value: string };

// One set's band. The axis decides the scale; kilos take one only from a MAX
// (kg / MAX = %); none and history place nothing on it.
const setBand = (set: ReadSet, ref: BandReference | undefined): RoundBand | null => {
  const v = set.value;
  if (v === null) return null;
  switch (set.kind) {
    case "percent-of-max":
      return { band: bandOf(v)!, value: fmt(v) };
    case "rpe":
      return { band: rpeBand(v), value: fmt(v) };
    case "rir":
      return { band: rirBand(v), value: fmt(v) };
    case "absolute": {
      const max = ref?.max?.valueKg;
      if (!ref || !max || max <= 0) return null;
      const pct = (toCanonicalKg(v, ref.cellUnit) / max) * 100;
      return { band: bandOf(pct)!, value: String(Math.round(pct)) };
    }
    default:
      return null;
  }
};

/** The band of one exercise in round `w`: its hardest set's, the first one
 *  on a tie. Null when it does not run there or no set has a band. */
export const slotRoundBand = (slot: SlotDraft, w: number, ref?: BandReference): RoundBand | null => {
  let best: RoundBand | null = null;
  for (const set of readRound(slot, w, false) ?? []) {
    const b = setBand(set, ref);
    if (b && (!best || BAND_RANK[b.band] > BAND_RANK[best.band])) best = b;
  }
  return best;
};

// Same shape as buildIntensityGrid(program): one row per session,
// cycleWeeks columns, hardest banded set colours the cell. Slots opted
// out of a week (inactiveWeeks[w]) contribute nothing there. `ref`: the
// MAXes by exercise and the cells' unit, so a row in kilos colours too.
export function buildIntensityGridFromDraft(
  draft: Draft,
  ref?: { maxes: ReadonlyMap<string, MaxForProposal> | undefined; cellUnit: UnitsPreference },
): IntensityGrid {
  const weeks = draft.cycleWeeks;
  const days = draft.sessions.map((session, sessionIdx) => {
    const cells: WeekCell[] = [];
    for (let w = 0; w < weeks; w += 1) {
      let cell: RoundBand | null = null;
      let sets = 0;
      for (const slot of session.slots) {
        sets += setCountAt(slot, w);
        const b = slotRoundBand(slot, w, ref && { max: ref.maxes?.get(slot.exerciseId), cellUnit: ref.cellUnit });
        if (b && (!cell || BAND_RANK[b.band] > BAND_RANK[cell.band])) cell = b;
      }
      cells.push({ band: cell?.band ?? null, value: cell?.value ?? null, sets });
    }
    const band = cells.reduce<IntensityBand | null>((acc, c) => higherBand(acc, c.band), null);
    return { sessionIdx, name: session.name, cells, band };
  });
  return { weeks, days };
}
